import { useMutation } from "@apollo/client";
import { FormattedMessage } from "react-intl";

import ExpertAvatar from "@components/ExpertAvatar";
import styles from "@components/NotificationCenter.module.css";
import Popover from "@components/popovers/Popover";
import PopoverItem from "@components/popovers/PopoverItem";
import BodyCopy from "@components/typography/BodyCopy";
import InternalLink from "@components/typography/InternalLink";
import useMyClient from "@hooks/useMyClient";
import useOpener, { Opener } from "@hooks/useOpener";
import {
  GetJourneyForMessagesQuery,
  NotificationKindEnum,
  NotificationMarkReadDocument,
  NotificationMarkReadMutation,
  NotificationMarkReadMutationVariables
} from "@typing/Generated";
import { sortByTimestamp } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";
import { daysAgo } from "@utils/timeUtils";

type Props = {
  journey: GetJourneyForMessagesQuery["journey"];
  opener: Opener;
};

const NotificationCenter = ({ journey, opener }: Props) => {
  const myClient = useMyClient();
  const popoverOpener = useOpener();

  const [markNotificationRead] = useMutation<NotificationMarkReadMutation, NotificationMarkReadMutationVariables>(
    NotificationMarkReadDocument
  );

  const markAllAsRead = () => {
    Promise.all(
      journey.notifications.map(notification =>
        markNotificationRead({
          variables: {
            id: notification.id
          }
        })
      )
    ).then(() => popoverOpener.close());
  };

  const linkForNotification = (notification: GetJourneyForMessagesQuery["journey"]["notifications"][0]) => {
    if (notification.kind === NotificationKindEnum.CARE_PLAN_PUBLISHED) {
      return journeyRoute({
        journeyId: journey.id,
        linkAfterJourney: "/care-plan",
        myClient
      });
    } else if (notification.kind === NotificationKindEnum.GOAL_UPDATED && notification.resource.__typename === "Goal") {
      return journeyRoute({
        journeyId: journey.id,
        linkAfterJourney: `/care-plan`,
        myClient
      });
    } else if (
      notification.kind === NotificationKindEnum.TASK_COMPLETED &&
      notification.resource.__typename === "Task"
    ) {
      return journeyRoute({
        journeyId: journey.id,
        linkAfterJourney: `/client-visible-tasks/${notification.resource.id}`,
        myClient
      });
    }
    return journeyRoute({
      journeyId: journey.id,
      linkAfterJourney: "/care-plan",
      myClient
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center">
        <BodyCopy color="gray-300" size="small">
          <FormattedMessage id="components.NotificationCenter.fromCarePlan" />
        </BodyCopy>
        <Popover icon="ellipsisV" isOpen={popoverOpener.isOpen} opener={popoverOpener}>
          <PopoverItem icon="bookOpen" onClick={markAllAsRead} textKey="components.NotificationCenter.markAsRead" />
        </Popover>
      </div>
      {sortByTimestamp(journey.notifications, n => n.createdAt).map((notification, index) => (
        <div
          className={`${styles.notificationContainer} ${index === journey.notifications.length - 1 ? styles.noBorder : ""}`}
          key={notification.id}
        >
          <InternalLink
            className="full-width"
            linkEventCreateVariables={{ journeyId: journey.id }}
            onClick={() => {
              markNotificationRead({
                variables: {
                  id: notification.id
                }
              }).then(() => opener.close());
            }}
            stylized={false}
            to={linkForNotification(notification)}
          >
            <div className={`flex gap-8 items-center ${styles.notification}`}>
              {journey.primaryExpert && <ExpertAvatar expert={journey.primaryExpert} size={36} />}
              <div className="flex justify-between items-center full-width gap-md">
                <div>
                  <BodyCopy bold={!!!notification.readAt}>
                    <FormattedMessage
                      id={`components.NotificationCenter.kinds.${notification.kind}`}
                      values={{
                        title:
                          notification.resource.__typename === "Goal" ? (
                            <BodyCopy bold={!!!notification.readAt} color="primary-300">
                              {notification.resource.goalTitle}
                            </BodyCopy>
                          ) : notification.resource.__typename === "Task" ? (
                            <BodyCopy bold={!!!notification.readAt} color="primary-300">
                              {notification.resource.taskTitle}
                            </BodyCopy>
                          ) : undefined
                      }}
                    />
                  </BodyCopy>
                </div>
                <div className="flex gap-sm items-center">
                  <BodyCopy className="no-wrap" size="small">
                    {daysAgo(notification.createdAt)}
                  </BodyCopy>
                  {!notification.readAt && <div className={styles.unread} />}
                </div>
              </div>
            </div>
          </InternalLink>
        </div>
      ))}
    </div>
  );
};

export default NotificationCenter;
